import type { Rule } from './types';

export const lexicalRules: Rule[] = [
  {
    id: 'lexical.imperative-ratio',
    category: 'lexical',
    severity: 'warn',
    weight: 5,
    title: 'Instructions use imperative voice',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.bodyText.sentenceCount < 4) {
        return { status: 'skipped', message: 'Body too short to evaluate.' };
      }
      const r = ctx.bodyText.imperativeRatio;
      if (r >= 0.35) return { status: 'pass', message: `Imperative ratio ${(r * 100).toFixed(0)}% (excellent).` };
      if (r >= 0.18) return { status: 'pass', message: `Imperative ratio ${(r * 100).toFixed(0)}%.` };
      return {
        status: 'warn',
        message: `Only ${(r * 100).toFixed(0)}% of sentences are imperative.`,
        fix: 'Use direct commands: "Run X", "Verify Y", "Return Z". Avoid descriptive narration.',
      };
    },
  },
  {
    id: 'lexical.low-hedging',
    category: 'lexical',
    severity: 'warn',
    weight: 3,
    title: 'Avoids hedging language',
    appliesTo: 'both',
    run(ctx) {
      const total = ctx.bodyText.hedgeCount + ctx.descText.hedgeCount;
      const wc = Math.max(1, ctx.bodyText.wordCount + ctx.descText.wordCount);
      const ratio = (total * 1000) / wc;
      if (total <= 1) return { status: 'pass', message: 'No hedging.' };
      if (ratio < 2) return { status: 'pass', message: `${total} hedge(s) (low density).` };
      if (ratio < 5) {
        return {
          status: 'warn',
          message: `${total} hedge words (might, maybe, probably, …).`,
          fix: 'Replace hedges with concrete commitments. Claude follows confident instructions better.',
        };
      }
      return {
        status: 'fail',
        message: `Heavy hedging: ${total} instances.`,
        fix: 'Rewrite to be definite: replace "might" with "should", "probably" with "always".',
      };
    },
  },
  {
    id: 'lexical.low-filler',
    category: 'lexical',
    severity: 'warn',
    weight: 2,
    title: 'Avoids filler words',
    appliesTo: 'both',
    run(ctx) {
      const total = ctx.bodyText.fillerCount + ctx.descText.fillerCount;
      const wc = Math.max(1, ctx.bodyText.wordCount + ctx.descText.wordCount);
      const ratio = (total * 1000) / wc;
      if (ratio < 4) return { status: 'pass', message: `${total} filler words (acceptable).` };
      if (ratio < 10) {
        return {
          status: 'warn',
          message: `${total} filler words (very, really, just, basically, …).`,
          fix: 'Strip empty intensifiers. Each one costs tokens with no signal.',
        };
      }
      return {
        status: 'fail',
        message: `Heavy filler density: ${total} instances.`,
        fix: 'Trim filler ("very", "really", "basically"); these waste tokens and weaken instructions.',
      };
    },
  },
  {
    id: 'lexical.passive-voice',
    category: 'lexical',
    severity: 'info',
    weight: 2,
    title: 'Active voice in instructions',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.bodyText.sentenceCount < 4) {
        return { status: 'skipped', message: 'Body too short.' };
      }
      const r = ctx.bodyText.passiveRatio;
      if (r < 0.15) return { status: 'pass', message: `Passive ratio ${(r * 100).toFixed(0)}%.` };
      if (r < 0.3) {
        return {
          status: 'warn',
          message: `Passive ratio ${(r * 100).toFixed(0)}%.`,
          fix: 'Prefer active voice: "Validate the input" over "the input should be validated".',
        };
      }
      return {
        status: 'warn',
        message: `Heavy passive voice (${(r * 100).toFixed(0)}%).`,
        fix: 'Rewrite to active voice. Imperative sentences are easier for Claude to act on.',
      };
    },
  },
  {
    id: 'lexical.sentence-length',
    category: 'lexical',
    severity: 'info',
    weight: 2,
    title: 'Sentences are concise',
    appliesTo: 'both',
    run(ctx) {
      const avg = ctx.bodyText.avgWordsPerSentence;
      const longest = ctx.bodyText.longestSentenceWords;
      if (avg === 0) return { status: 'skipped', message: 'Empty body.' };
      if (avg > 28 || longest > 60) {
        return {
          status: 'warn',
          message: `Average sentence ${avg.toFixed(0)} words (longest ${longest}).`,
          fix: 'Aim for 12–22 words/sentence. Long sentences obscure imperatives.',
        };
      }
      if (avg < 6) {
        return {
          status: 'warn',
          message: `Very short sentences (avg ${avg.toFixed(0)} words).`,
          fix: 'Combine related steps so the intent is unambiguous.',
        };
      }
      return { status: 'pass', message: `Avg ${avg.toFixed(0)} words/sentence.` };
    },
  },
  {
    id: 'lexical.no-first-person-body',
    category: 'lexical',
    severity: 'info',
    weight: 1,
    title: 'Body avoids first-person',
    appliesTo: 'both',
    run(ctx) {
      const n = ctx.bodyText.firstPersonCount;
      if (n === 0) return { status: 'pass', message: 'No first-person pronouns.' };
      if (n <= 2) return { status: 'pass', message: `${n} first-person token(s) (low).` };
      return {
        status: 'warn',
        message: `${n} first-person pronouns ("I", "we", "our").`,
        fix: 'Address Claude in imperative form, not the author in narrative.',
      };
    },
  },
  {
    id: 'lexical.unique-token-ratio',
    category: 'lexical',
    severity: 'info',
    weight: 1,
    title: 'Vocabulary diversity',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.bodyText.wordCount < 200) return { status: 'skipped', message: 'Body too short.' };
      const r = ctx.bodyText.uniqueLowercaseTokenRatio;
      if (r >= 0.34) return { status: 'pass', message: `Type/token ratio ${(r * 100).toFixed(0)}%.` };
      return {
        status: 'warn',
        message: `Low vocabulary diversity (${(r * 100).toFixed(0)}%).`,
        fix: 'Repetitive phrasing wastes tokens. Vary phrasing or compress repetition.',
      };
    },
  },
  {
    id: 'lexical.token-budget',
    category: 'lexical',
    severity: 'warn',
    weight: 4,
    title: 'Token budget is healthy',
    appliesTo: 'both',
    run(ctx) {
      const total = ctx.bodyText.estimatedTokens + ctx.descText.estimatedTokens;
      if (total > 8000) {
        return {
          status: 'fail',
          message: `~${total} tokens estimated (over 8k).`,
          fix: 'Aggressively trim or offload to `references/`. This blows the context budget.',
        };
      }
      if (total > 4500) {
        return {
          status: 'warn',
          message: `~${total} tokens estimated.`,
          fix: 'Move detail to `references/` to stay under the 5k-word / ~4k-token guideline.',
        };
      }
      return { status: 'pass', message: `~${total} tokens.` };
    },
  },
  {
    id: 'lexical.no-overpromising',
    category: 'lexical',
    severity: 'warn',
    weight: 2,
    title: 'Avoids overpromising language',
    appliesTo: 'both',
    run(ctx) {
      const text = (ctx.parsed.frontmatter.description as string || '') + '\n' + ctx.parsed.body;
      const flags = [
        /\bmagically\b/i,
        /\bautomatically handles everything\b/i,
        /\balways works\b/i,
        /\bnever fails\b/i,
        /\bperfect(?:ly)?\b/i,
        /\bworld[- ]class\b/i,
        /\bbest[- ]in[- ]class\b/i,
        /\bstate[- ]of[- ]the[- ]art\b/i,
      ];
      const hits = flags.filter((re) => re.test(text));
      if (hits.length === 0) return { status: 'pass', message: 'No overpromising.' };
      return {
        status: 'warn',
        message: `${hits.length} overpromising phrase(s) detected.`,
        fix: 'Replace marketing language with concrete capabilities.',
      };
    },
  },
  {
    id: 'lexical.no-duplicate-paragraphs',
    category: 'lexical',
    severity: 'warn',
    weight: 2,
    title: 'No duplicated paragraphs',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.arch.duplicateParagraphs === 0) {
        return { status: 'pass', message: 'No duplicated paragraphs.' };
      }
      return {
        status: 'warn',
        message: `${ctx.arch.duplicateParagraphs} duplicated paragraph(s).`,
        fix: 'De-duplicate. Repeated content costs tokens without adding signal.',
      };
    },
  },
];
